import type { CostSummary } from './costs';
import type { SessionSettings } from './settings';
import type { TranscriptSession } from './transcript';
import type { Stats } from '@atc/sim';

const ID_KEY = 'atc.anonymous-id.v1';
export const NOTICE_KEY = 'atc.data-notice.v1';
const makeId = () => globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function anonymousId(storage: Pick<Storage, 'getItem' | 'setItem'> | undefined): string {
  try {
    const found = storage?.getItem(ID_KEY);
    if (found && /^[A-Za-z0-9_-]{8,100}$/.test(found)) return found;
    const next = makeId(); storage?.setItem(ID_KEY, next); return next;
  } catch { return makeId(); }
}

export interface TelemetrySnapshot {
  settings: SessionSettings;
  transcript: TranscriptSession;
  stats: Stats & { idlePauses?: number };
  costs?: CostSummary;
  errors?: readonly ClientDiagnostic[];
}

export interface ClientDiagnostic { at: string; message: string; stack?: string; route: string; }

export function captureDiagnostics(): { values: ClientDiagnostic[]; dispose(): void } {
  const values: ClientDiagnostic[] = [];
  const add = (message: string, stack?: string) => {
    if (values.some((v) => v.message === message && v.stack === stack)) return;
    values.push({ at: new Date().toISOString(), message: message.slice(0, 1000), ...(stack ? { stack: stack.slice(0, 8000) } : {}), route: location.hash });
    if (values.length > 20) values.shift();
  };
  const error = (event: ErrorEvent) => add(event.message, event.error instanceof Error ? event.error.stack : undefined);
  const rejection = (event: PromiseRejectionEvent) => add(event.reason instanceof Error ? event.reason.message : String(event.reason), event.reason instanceof Error ? event.reason.stack : undefined);
  window.addEventListener('error', error); window.addEventListener('unhandledrejection', rejection);
  return { values, dispose() { window.removeEventListener('error', error); window.removeEventListener('unhandledrejection', rejection); } };
}

export interface TelemetrySession {
  checkpoint(): void; finish(): void; abandon(): void; dispose(): void;
  /** While quiet (the game paused itself) the periodic checkpoint is skipped: nothing is changing. */
  setQuiet(quiet: boolean): void;
}

export function startTelemetry(anonymous: string, snapshot: () => TelemetrySnapshot, enabled = true, context: Record<string, unknown> = {}): TelemetrySession {
  let ended = false, quiet = false, busy = false, queued: 'checkpoint' | 'end' | 'abandon' | null = null;
  const send = (stage: 'start' | 'checkpoint' | 'end' | 'abandon', keepalive = false) => {
    if (!enabled || (ended && stage !== 'end' && stage !== 'abandon')) return;
    if (busy) { queued = stage === 'end' || stage === 'abandon' ? stage : queued ?? 'checkpoint'; return; }
    const value = snapshot();
    // Where the player is, when Cloudflare is not in front to say so. A timezone, not a
    // location: it is what the browser already tells every site, and it is not an address.
    const tz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return undefined; } })();
    const payload = { schemaVersion: 1, id: value.transcript.id, anonymousId: anonymous, stage, tz, startedAt: value.transcript.startedAt, ...(value.transcript.endedAt ? { endedAt: value.transcript.endedAt } : {}), airport: value.settings.airport, mode: context.lessonId ? 'academy' : value.settings.mode, context, settings: value.settings, stats: value.stats, transcript: value.transcript, costs: value.costs, errors: value.errors ?? [] };
    busy = true;
    void fetch('/api/telemetry/sessions', { method: 'POST', headers: { 'content-type': 'application/json', 'x-atc-client': '1' }, body: JSON.stringify(payload), keepalive }).catch(() => {}).finally(() => { busy = false; const next = queued; queued = null; if (next) send(next, next !== 'checkpoint'); });
  };
  send('start');
  const timer = window.setInterval(() => { if (!quiet) send('checkpoint'); }, 30000);
  const pagehide = () => { if (!ended) send('abandon', true); };
  window.addEventListener('pagehide', pagehide);
  return {
    checkpoint() { send('checkpoint'); },
    setQuiet(value) { quiet = value; },
    finish() { if (ended) return; ended = true; send('end', true); },
    abandon() { if (ended) return; ended = true; send('abandon', true); },
    dispose() { clearInterval(timer); window.removeEventListener('pagehide', pagehide); },
  };
}

export async function submitFeedback(input: { anonymousId: string; sessionId?: string; kind: 'bug' | 'feature' | 'general'; message: string; context: Record<string, unknown> }): Promise<void> {
  const response = await fetch('/api/feedback', { method: 'POST', headers: { 'content-type': 'application/json', 'x-atc-client': '1' }, body: JSON.stringify({ id: makeId(), ...input }) });
  if (!response.ok) { const value = await response.json().catch(() => ({})); throw new Error(value.error ?? 'Feedback could not be sent.'); }
}
